// =============================================================================
// paris-time.ts — Conversions ISO UTC <-> heure de Paris pour la saisie des dates de campagne.
// =============================================================================
// GATE_HUMAN 2026-09-16 (calendrier, réponse 5b) : la saisie ET l'affichage des dates de campagne et de
// cycles se font en Europe/Paris, QUEL QUE SOIT le fuseau du navigateur (libellé « Heure de Paris »).
//
// Remplace, pour le calendrier du détail campagne, l'ancien `isoToLocalInput` qui relisait l'ISO en UTC
// (`toISOString().slice(0, 16)`) alors que `new Date(valeur)` l'interprétait en heure LOCALE : un aller-
// retour décalait l'heure de 2 h en été. Ici les deux sens passent par le même fuseau explicite.
//
// Aucune dépendance : uniquement `Intl.DateTimeFormat` (base IANA embarquée par le moteur JS). Les
// changements d'heure (dernier dimanche de mars / d'octobre) sont résolus par la base IANA.

const PARIS = 'Europe/Paris';

const PARTS_FORMAT = new Intl.DateTimeFormat('en-GB', {
  timeZone: PARIS,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

interface WallClock {
  readonly year: number;
  readonly month: number;
  readonly day: number;
  readonly hour: number;
  readonly minute: number;
  readonly second: number;
}

/** Heure « murale » de Paris pour un instant donné (ms epoch). */
function parisWallClock(epochMs: number): WallClock {
  const parts = PARTS_FORMAT.formatToParts(new Date(epochMs));
  const get = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((p) => p.type === type)?.value ?? '0');
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: get('hour'),
    minute: get('minute'),
    second: get('second'),
  };
}

/** Décalage de Paris par rapport à UTC (ms) à l'instant donné : +1 h en hiver, +2 h en été. */
function parisOffsetMs(epochMs: number): number {
  const w = parisWallClock(epochMs);
  const asUtc = Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second);
  // L'instant d'entrée peut porter des millisecondes que la montre murale ignore.
  return asUtc - (epochMs - (((epochMs % 1000) + 1000) % 1000));
}

const pad = (n: number): string => String(n).padStart(2, '0');

/**
 * ISO 8601 (ou null) → valeur d'un `<input type="datetime-local">` exprimée en heure de Paris
 * (« YYYY-MM-DDTHH:mm »). `null`, vide ou invalide ⇒ chaîne vide.
 */
export function isoToParisInput(iso: string | null): string {
  if (iso === null || iso.trim().length === 0) {
    return '';
  }
  const epoch = new Date(iso).getTime();
  if (Number.isNaN(epoch)) {
    return '';
  }
  const w = parisWallClock(epoch);
  return `${String(w.year)}-${pad(w.month)}-${pad(w.day)}T${pad(w.hour)}:${pad(w.minute)}`;
}

const INPUT_PATTERN = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/;

/**
 * Valeur « YYYY-MM-DDTHH:mm » lue comme heure de Paris → ISO 8601 UTC. Vide ou invalide ⇒ `null`.
 *
 * Résolution en deux passes : on devine l'instant en appliquant le décalage de Paris au même horaire
 * lu en UTC, puis on corrige avec le décalage valable à l'instant deviné (changement d'heure entre les
 * deux). Heure inexistante (saut de mars, 02:30) : l'instant retenu est celui qui suit le saut.
 * Heure ambiguë (retour d'octobre, 02:30) : l'une des deux occurrences, de façon déterministe.
 */
export function parisInputToIso(value: string): string | null {
  const m = INPUT_PATTERN.exec(value.trim());
  if (m === null) {
    return null;
  }
  const [, y, mo, d, h, mi, s] = m;
  const naiveUtc = Date.UTC(
    Number(y),
    Number(mo) - 1,
    Number(d),
    Number(h),
    Number(mi),
    Number(s ?? '0'),
  );
  if (Number.isNaN(naiveUtc)) {
    return null;
  }
  const firstGuess = naiveUtc - parisOffsetMs(naiveUtc);
  const resolved = naiveUtc - parisOffsetMs(firstGuess);
  return new Date(resolved).toISOString();
}

/** Horodatage ISO → « JJ/MM/AAAA HH:mm » en heure de Paris ; « — » si null/invalide. */
export function formatParis(iso: string | null): string {
  const input = isoToParisInput(iso);
  if (input.length === 0) {
    return '—';
  }
  return formatParisInput(input);
}

/** Valeur « YYYY-MM-DDTHH:mm » (déjà en heure de Paris) → « JJ/MM/AAAA HH:mm » ; « — » si invalide. */
export function formatParisInput(value: string): string {
  const m = INPUT_PATTERN.exec(value.trim());
  if (m === null) {
    return '—';
  }
  const [, y, mo, d, h, mi] = m;
  return `${d ?? ''}/${mo ?? ''}/${y ?? ''} ${h ?? ''}:${mi ?? ''}`;
}
